import {
  clearSyncCursor,
  emptyGraph,
  isMutDirty,
  linkIdentityEquals,
  recordsEqual,
  remoteLinkOf,
  resolveRemoteLink,
  stripDeviceLocalLink,
  stripDeviceMutState,
  type AppPreferencesRecord,
  type DeviceMutState,
  type SyncableRecord,
  type SyncGraph,
  type Task,
  type TaskRemoteLink,
} from "@app/core";
import { applySyncGraphBulk, canApplySyncGraphBulk } from "@app/storage";
import { getServices } from "../platform.js";
import {
  applyPreferencesSyncRecords,
  currentPreferencesSyncRecord,
  gatherPreferencesSyncRecords,
} from "./preferences-record.js";
import { PREFERENCES_ACKED_AT_KEY } from "./sync-metadata.js";

interface ApplyProgress {
  done: number;
  total: number;
  kind: keyof SyncGraph;
}

type ApplyProgressFn = (progress: ApplyProgress) => void;

/** The row kinds that live in repositories (everything except preferences). */
const REPO_KINDS = [
  "calendars",
  "topics",
  "labels",
  "recurrenceRules",
  "tasks",
  "recurrenceOverrides",
] as const;
type RepoKind = (typeof REPO_KINDS)[number];

interface MutRepo<T> {
  listAll(): Promise<T[]>;
  insert(r: T, synced?: "server" | "google"): Promise<void>;
  update(r: T, synced?: "server" | "google"): Promise<void>;
  ackMut(id: string, dest: "server" | "google", upTo: number): Promise<void>;
}

function repoFor(kind: RepoKind): MutRepo<SyncableRecord & DeviceMutState> {
  const s = getServices();
  const map = {
    calendars: s.calendars,
    topics: s.topics,
    labels: s.labels,
    recurrenceRules: s.recurrence,
    tasks: s.tasks,
    recurrenceOverrides: s.recurrenceOverrides,
  };
  return map[kind] as unknown as MutRepo<SyncableRecord & DeviceMutState>;
}

/**
 * A point-in-time read of the full local graph, in BOTH shapes the sync needs:
 * `graph` is transmittable (device-local fields stripped — the merge input and
 * push payload), `raw` keeps every row's `mut`/`*_acked_mut` counters. Both come
 * from the SAME repository read, which is what makes `raw` a valid apply
 * BASELINE: a row whose current `mut` differs from its `raw` copy was written
 * AFTER this gather, i.e. mid-sync, and must not be overwritten by the apply.
 */
export interface LocalSyncState {
  graph: SyncGraph;
  raw: SyncGraph;
}

/** Baseline for {@link applySyncGraph}: `${kind}:${id}` → the row's `mut` at
 *  gather time. A row absent here did not exist when the sync pass started. */
export type MutBaseline = ReadonlyMap<string, number>;

export function mutBaseline(raw: SyncGraph): MutBaseline {
  const m = new Map<string, number>();
  for (const kind of REPO_KINDS) {
    for (const r of raw[kind] as unknown as (SyncableRecord & DeviceMutState)[]) {
      m.set(`${kind}:${r.id}`, r.mut ?? 0);
    }
  }
  return m;
}

export async function gatherLocalSyncState(): Promise<LocalSyncState> {
  const { tasks, recurrence, recurrenceOverrides, labels, topics, calendars } = getServices();
  const raw: SyncGraph = {
    tasks: await tasks.listAll(),
    recurrenceRules: await recurrence.listAll(),
    recurrenceOverrides: await recurrenceOverrides.listAll(),
    labels: await labels.listAll(),
    topics: await topics.listAll(),
    calendars: await calendars.listAll(),
    // Theme is NOT carried by the generic dirty/apply path — it has a dedicated
    // conflict-aware resolver (`features/theme/theme-sync.ts`). Kept empty here so
    // mergeGraph/applySyncGraph never blind-LWW it.
    themes: [],
    preferences: await gatherPreferencesSyncRecords(),
  };
  return { graph: stripGraphForWire(raw), raw };
}

/** Strip every device-local field for the wire: the Google `etag` (this
 *  device's If-Match handle — a peer's copy is stale the moment either device
 *  pushes), the per-calendar provider cursors, and the `mut` counters (a peer
 *  adopting them would believe it had already synced writes it never made). */
function stripGraphForWire(g: SyncGraph): SyncGraph {
  return {
    ...g,
    tasks: g.tasks.map(stripDeviceLocalLink),
    recurrenceRules: g.recurrenceRules.map(stripDeviceMutState),
    recurrenceOverrides: g.recurrenceOverrides.map(stripDeviceMutState),
    labels: g.labels.map(stripDeviceMutState),
    topics: g.topics.map(stripDeviceMutState),
    calendars: g.calendars.map((c) => stripDeviceMutState(clearSyncCursor(c))),
  };
}

/**
 * The local rows the sync server has not acknowledged — the "offline queue",
 * read straight off each row: dirty === `mut > server_acked_mut`. Offline edits
 * simply accumulate (each write bumps `mut`); this selects them on reconnect.
 * There is no timestamp or watermark anywhere in the decision, so a clock
 * correction cannot hide an edit and a link adoption (which bumps `mut` without
 * touching `updated_at`) queues like any other write. The preferences singleton
 * uses the same value-equality idea against its own clock: dirty === its
 * `preferences_updated_at` differs from the last acknowledged one.
 */
export function serverDirtyGraph(raw: SyncGraph, prefsDirty: AppPreferencesRecord[]): SyncGraph {
  const dirty = <T extends DeviceMutState>(rows: T[]): T[] =>
    rows.filter((r) => isMutDirty(r, "server_acked_mut"));
  return {
    ...emptyGraph(),
    tasks: dirty(raw.tasks).map(stripDeviceLocalLink),
    recurrenceRules: dirty(raw.recurrenceRules).map(stripDeviceMutState),
    recurrenceOverrides: dirty(raw.recurrenceOverrides).map(stripDeviceMutState),
    labels: dirty(raw.labels).map(stripDeviceMutState),
    topics: dirty(raw.topics).map(stripDeviceMutState),
    calendars: dirty(raw.calendars).map((c) => stripDeviceMutState(clearSyncCursor(c))),
    preferences: prefsDirty,
  };
}

/** The preferences rows to push: the singleton, when its clock differs from the
 *  last server-acknowledged one (value inequality, not ordering — remote-adopted
 *  clocks may be older than a stale ack). */
export async function dirtyPreferences(): Promise<AppPreferencesRecord[]> {
  const record = await currentPreferencesSyncRecord();
  if (!record) return [];
  const acked = (await getServices().settings.get(PREFERENCES_ACKED_AT_KEY)) || null;
  return record.updated_at === acked ? [] : [record];
}

/** Record that the server now holds the preferences record with this clock. */
async function ackPreferences(updatedAt: string): Promise<void> {
  await getServices().settings.set(PREFERENCES_ACKED_AT_KEY, updatedAt);
}

/**
 * Acknowledge a successfully pushed batch: each row's `server_acked_mut`
 * advances to the `mut` its content was READ at (from `raw`, the same gather the
 * push payload came from) — clamped by the repo so a write that landed while the
 * push was in flight keeps the row dirty. Doing this per row from durable state
 * is what survives a crash between the push and any later bookkeeping.
 */
export async function ackServerPush(
  raw: SyncGraph,
  pushed: SyncGraph,
  beforeWrite?: () => void,
): Promise<void> {
  for (const kind of REPO_KINDS) {
    const pushedRows = pushed[kind] as unknown as SyncableRecord[];
    if (pushedRows.length === 0) continue;
    const rawById = new Map(
      (raw[kind] as unknown as (SyncableRecord & DeviceMutState)[]).map((r) => [r.id, r] as const),
    );
    const repo = repoFor(kind);
    for (const row of pushedRows) {
      const rawRow = rawById.get(row.id);
      if (rawRow) {
        beforeWrite?.();
        await repo.ackMut(row.id, "server", rawRow.mut ?? 0);
        beforeWrite?.();
      }
    }
  }
  const prefs = pushed.preferences[0];
  if (prefs) {
    beforeWrite?.();
    await ackPreferences(prefs.updated_at);
    beforeWrite?.();
  }
}

/** Everything except the wholesale write: rows that must not be overwritten
 *  (edited mid-sync), rows whose write would be a no-op, and the two per-row
 *  adoptions (server rev, resolved remote link) that ride alongside. */
interface ApplyPlan {
  writes: SyncGraph;
  revAdopts: { id: string; rev: number }[];
  linkAdopts: { id: string; link: TaskRemoteLink; linkRev: number }[];
}

/**
 * Upsert a merged/authoritative graph into local storage, in dependency order.
 * Tombstone rows (deleted_at set) write through too. Three guards, all derived
 * from durable per-row facts:
 *
 * 1. MID-SYNC SHIELD. A row whose current `mut` differs from its `baseline`
 *    (gather-time) `mut` was written while the sync was in flight. It is NOT
 *    overwritten — blind-upserting destroyed the edit AND un-dirtied it. The
 *    kept row stays server-dirty (its edit was never acked), so this pass's
 *    push sends it; for tasks the server's `rev` is adopted rev-only so that
 *    push wins the rev-first comparison instead of being discarded as stale.
 *    The old shield guessed "edited mid-sync" from `updated_at` ordering, which
 *    same-ms stamps and clock skew both defeat — `mut` is a fact.
 *
 * 2. SKIP-IDENTICAL. A row whose content already equals the incoming one is not
 *    rewritten (rev/link adoptions still land, without a `mut` bump). This is
 *    not an optimization: every content write bumps `mut`, which would make the
 *    row GOOGLE-dirty — so applying a no-op server echo would re-PATCH every
 *    pulled row's Google event, every pass.
 *
 * 3. SELF-ACK. Rows that DO write are self-acknowledged for the server in the
 *    same statement (`server_acked_mut = mut+1`): their content came from the
 *    server, so it is in step by construction — and they stay google-dirty,
 *    which is exactly right, because a genuinely-changed row from a peer must
 *    propagate onward to Google.
 */
export async function applySyncGraph(
  g: SyncGraph,
  baseline: MutBaseline,
  onProgress?: ApplyProgressFn,
  beforeWrite?: () => void,
): Promise<void> {
  beforeWrite?.();
  const plan = await planApply(g, baseline, beforeWrite);
  beforeWrite?.();
  const db = getServices().db;
  const progress = new ProgressCounter(graphCount(plan.writes) + g.preferences.length, onProgress);

  if (canApplySyncGraphBulk(db) && graphCount(plan.writes) > 0) {
    await applySyncGraphBulk(db, plan.writes, onProgress, beforeWrite);
  } else {
    await applyWritesByLoop(plan.writes, progress, beforeWrite);
  }
  const tasks = getServices().tasks;
  for (const { id, rev } of plan.revAdopts) {
    beforeWrite?.();
    await tasks.adoptRev(id, rev);
    beforeWrite?.();
  }
  for (const { id, link, linkRev } of plan.linkAdopts) {
    beforeWrite?.();
    await tasks.applyRemoteLink(id, link, linkRev);
    beforeWrite?.();
  }

  await applyIncomingPreferences(g.preferences, beforeWrite);
  for (const _ of g.preferences) progress.tick("preferences");
  beforeWrite?.();
}

async function planApply(
  g: SyncGraph,
  baseline: MutBaseline,
  beforeWrite?: () => void,
): Promise<ApplyPlan> {
  const writes: SyncGraph = { ...emptyGraph() };
  const revAdopts: ApplyPlan["revAdopts"] = [];
  const linkAdopts: ApplyPlan["linkAdopts"] = [];

  for (const kind of REPO_KINDS) {
    const incoming = g[kind] as unknown as (SyncableRecord & DeviceMutState)[];
    if (incoming.length === 0) continue;
    const repo = repoFor(kind);
    beforeWrite?.();
    const current = new Map((await repo.listAll()).map((r) => [r.id, r] as const));
    beforeWrite?.();
    const out = writes[kind] as unknown as SyncableRecord[];

    for (const row of incoming) {
      const cur = current.get(row.id);
      if (!cur) {
        out.push(row);
        continue;
      }
      const base = baseline.get(`${kind}:${row.id}`);
      if ((cur.mut ?? 0) !== base) {
        // Mid-sync local write (or a row created after the gather): keep local.
        if (kind === "tasks" && typeof row.rev === "number" && row.rev !== cur.rev) {
          revAdopts.push({ id: row.id, rev: row.rev });
        }
        continue;
      }
      // UNPUSHED LOCAL CONTENT THAT STILL DESERVES TO COMPETE. The incoming row
      // won the merge rev-first (a peer pushed, advancing the server rev, while
      // this row's edit sat unpushed at the old rev) — but wall-clock LWW says
      // the local content is newer. Keep it and adopt the incoming rev: the row
      // is dirty, so this pass's push sends it, and at the adopted rev the
      // server's tie-break falls through to `updated_at`, which the local edit
      // wins — on the server and every peer, not just here. Blindly applying
      // would discard a newer edit merely for having lost the race to push.
      // (A row with nothing unacknowledged has no such claim: the incoming
      // rev-authoritative copy applies.)
      if (isMutDirty(cur, "server_acked_mut") && cur.updated_at > row.updated_at) {
        if (kind === "tasks" && typeof row.rev === "number" && row.rev !== cur.rev) {
          revAdopts.push({ id: row.id, rev: row.rev });
        }
        continue;
      }
      const resolved =
        kind === "tasks" ? resolveRemoteLink(row as Task, cur as Task) : (row as typeof cur);
      if (contentEquals(kind, cur, resolved)) {
        if (kind === "tasks") {
          const curTask = cur as Task;
          const resTask = resolved as Task;
          if (typeof resTask.rev === "number" && resTask.rev !== curTask.rev) {
            revAdopts.push({ id: curTask.id, rev: resTask.rev });
          }
          if (
            !linkIdentityEquals(curTask, resTask) ||
            (curTask.link_rev ?? 0) !== (resTask.link_rev ?? 0)
          ) {
            linkAdopts.push({
              id: curTask.id,
              link: remoteLinkOf(resTask),
              linkRev: resTask.link_rev ?? 0,
            });
          }
        }
        continue;
      }
      out.push(resolved);
    }
  }
  return { writes, revAdopts, linkAdopts };
}

/** Compare a stored row against the incoming one on the fields the apply would
 *  actually write: no device-local fields, no `rev` (adopted separately), no
 *  remote-link block (resolved by `link_rev`, adopted separately), no calendar
 *  cursor (device-local, preserved). */
function contentEquals(kind: RepoKind, a: SyncableRecord, b: SyncableRecord): boolean {
  return recordsEqual(normalizeForCompare(kind, a), normalizeForCompare(kind, b));
}

function normalizeForCompare(kind: RepoKind, row: SyncableRecord): Record<string, unknown> {
  const r = { ...stripDeviceMutState(row as SyncableRecord & DeviceMutState) } as Record<string, unknown>;
  delete r["rev"];
  if (kind === "tasks") {
    for (const f of ["external_id", "ical_uid", "etag", "source", "external_calendar_id", "link_rev"]) {
      delete r[f];
    }
  }
  if (kind === "calendars") {
    delete r["sync_token"];
    delete r["last_synced_at"];
  }
  return r;
}

/** Loop fallback for adapters without `executeBatch` (browser MemoryAdapter):
 *  the same self-acknowledging semantics, via each repo's `synced: "server"`
 *  write variant. Task link columns are excluded from `update` by design; the
 *  planner's `linkAdopts` are not needed here for written rows because inserts
 *  carry the resolved link and updates follow with `applyRemoteLink`. */
async function applyWritesByLoop(
  writes: SyncGraph,
  progress: ProgressCounter,
  beforeWrite?: () => void,
): Promise<void> {
  const tasksRepo = getServices().tasks;
  for (const kind of REPO_KINDS) {
    const rows = writes[kind] as unknown as (SyncableRecord & DeviceMutState)[];
    if (rows.length === 0) continue;
    const repo = repoFor(kind);
    beforeWrite?.();
    const existing = new Set((await repo.listAll()).map((r) => r.id));
    beforeWrite?.();
    for (const row of rows) {
      beforeWrite?.();
      if (existing.has(row.id)) {
        await repo.update(row, "server");
        beforeWrite?.();
        if (kind === "tasks") {
          const t = row as Task;
          beforeWrite?.();
          await tasksRepo.applyRemoteLink(t.id, remoteLinkOf(t), t.link_rev ?? 0);
          beforeWrite?.();
        }
      } else {
        await repo.insert(row, "server");
        beforeWrite?.();
      }
      progress.tick(kind);
    }
  }
}

/** Apply the newest incoming preferences record when it beats the local clock,
 *  then self-acknowledge — the adopted clock IS what the server holds. */
async function applyIncomingPreferences(
  rows: AppPreferencesRecord[],
  beforeWrite?: () => void,
): Promise<void> {
  if (rows.length === 0) return;
  beforeWrite?.();
  const current = await currentPreferencesSyncRecord();
  beforeWrite?.();
  const newest = rows
    .filter((r) => !r.deleted_at)
    .sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))[0];
  if (!newest) return;
  if (current && current.updated_at >= newest.updated_at) return;
  beforeWrite?.();
  await applyPreferencesSyncRecords([newest]);
  beforeWrite?.();
  await ackPreferences(newest.updated_at);
  beforeWrite?.();
}

/** Split a graph into batches of at most `size` total rows, preserving dependency
 *  order (calendars/topics/labels/rules before tasks/overrides). Used to push a
 *  large dirty set in pieces so the UI can show real X-of-Y upload progress; a
 *  graph already at/under `size` returns a single batch (no behavior change).
 *  The server merges each batch independently (per-record LWW, order-agnostic). */
export function chunkGraph(g: SyncGraph, size: number): SyncGraph[] {
  if (graphCount(g) <= size) return [g];
  const kinds = [
    "calendars",
    "topics",
    "labels",
    "recurrenceRules",
    "tasks",
    "recurrenceOverrides",
    "preferences",
  ] as const;
  const batches: SyncGraph[] = [];
  let current = emptyGraph();
  let count = 0;
  for (const kind of kinds) {
    for (const row of g[kind]) {
      (current[kind] as typeof g[typeof kind]).push(row as never);
      if (++count >= size) {
        batches.push(current);
        current = emptyGraph();
        count = 0;
      }
    }
  }
  if (count > 0) batches.push(current);
  return batches;
}

export function graphCount(g: SyncGraph): number {
  return (
    g.tasks.length +
    g.recurrenceRules.length +
    g.recurrenceOverrides.length +
    g.labels.length +
    g.topics.length +
    g.calendars.length +
    g.preferences.length
  );
}

class ProgressCounter {
  private done = 0;

  constructor(
    private readonly total: number,
    private readonly onProgress?: ApplyProgressFn,
  ) {}

  tick(kind: keyof SyncGraph): void {
    this.done++;
    if (this.done === this.total || this.done % 25 === 0) {
      this.onProgress?.({ done: this.done, total: this.total, kind });
    }
  }
}
